import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Plus,
  FileText,
  RefreshCw,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import { searchPrescriptions } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { toast } from '@/components/ui/use-toast';
import { Prescription } from '@/types/prescription';
import PrintPrescriptionPreviewModal from '@/components/Prescription/PrintPrescriptionPreviewModal';
import PrescriptionDataTable from '@/components/Prescription/PrescriptionDataTable';
import SimpleFilters, { EMPTY_FILTERS, type SimpleFilterState } from '@/components/Prescription/SimpleFilters';
import PatientSearchInput from '@/components/shared/PatientSearchInput';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';

export default function PrescriptionsPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => { document.title = "Prescriptions | OneCheckUp"; }, []);

  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Inline patient search for new prescription
  const [showPatientSearch, setShowPatientSearch] = useState<boolean>(false);
  const [patientSearchText, setPatientSearchText] = useState<string>('');

  // Simple filter states
  const [simpleFilters, setSimpleFilters] = useState<SimpleFilterState>(EMPTY_FILTERS);
  // Searched on the server; wait for a pause in typing
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(simpleFilters.searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [simpleFilters.searchQuery]);
  // How many match, and the doctor's totals for the header
  const [matchCount, setMatchCount] = useState(0);
  const [allTotal, setAllTotal] = useState(0);
  const [writtenToday, setWrittenToday] = useState(0);


  // One list, newest first, grouped by day; "Show more" loads the next batch
  const PAGE = 20;
  const [itemsPerPage, setItemsPerPage] = useState(PAGE);
  
  // Print modal state
  const [showPrintModal, setShowPrintModal] = useState<boolean>(false);
  const [prescriptionToPrint, setPrescriptionToPrint] = useState<Prescription | null>(null);

  const { currentUser } = useAuth();
  const { can } = usePermission();
  const canPrescribe = can('prescriptions:write');

  // Handle navigation state and URL parameters
  useEffect(() => {
    const handleNavigationContext = () => {
      // Handle URL search parameters
      const filterParam = searchParams.get('filter');
      const patientParam = searchParams.get('patient');
      const modeParam = searchParams.get('mode');
      
      // Handle navigation state
      const state = location.state;
      
      // Set filter based on URL parameter - use simple filters
      if (filterParam === 'today') {
        setSimpleFilters(prev => ({
          ...prev,
          dateFilter: 'today'
        }));
      }
      
      // Set filter based on navigation state
      if (state?.filter === 'patient' && state?.patientId) {
        // When coming from patient-specific view, focus on that patient
        const patientId = state.patientId;
        const patientName = state.patientName;
        if (patientName) {
          setSimpleFilters(prev => ({
            ...prev,
            searchQuery: patientName
          }));
        }
      }
      
      // Handle mode-specific behavior
      if (state?.mode === 'create' || modeParam === 'consultation') {
        // Could trigger create prescription modal/flow here
        // console.log('Create prescription mode activated');
      }
      
      // Handle focus search from dashboard
      if (state?.focusSearch) {
        // Focus the search input when component mounts
        setTimeout(() => {
          const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
          }
        }, 100);
      }
    };

    handleNavigationContext();
  }, [searchParams, location.state]);

  // The date filter as a range of prescription dates (yyyy-MM-dd)
  const dateBounds = useMemo(() => {
    const ymd = (d: Date) => format(d, 'yyyy-MM-dd');
    const { dateFilter, dateRange } = simpleFilters;
    if (dateRange.from || dateRange.to) {
      return { from: dateRange.from?.toISOString().slice(0, 10), to: dateRange.to?.toISOString().slice(0, 10) };
    }
    const now = new Date();
    if (dateFilter === 'today') return { from: ymd(now), to: ymd(now) };
    if (dateFilter === 'week')  return { from: ymd(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)), to: ymd(now) };
    if (dateFilter === 'month') return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to: ymd(now) };
    return { from: undefined, to: undefined };
  }, [simpleFilters]);

  const loadData = useCallback(async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    try {
      setError(null);
      const data = await searchPrescriptions({
        clinic_id: currentUser.clinic_id,
        doctor_id: currentUser.doctor_id,
        q: debouncedQuery || undefined,
        date_from: dateBounds.from,
        date_to: dateBounds.to,
        limit: itemsPerPage,
      });
      if (data) {
        setPrescriptions(data.prescriptions);
        setMatchCount(data.total);
        setAllTotal(data.all_total);
        setWrittenToday(data.today);
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to load prescriptions. Please try again.';
      setError(errorMessage);
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [currentUser?.doctor_id, currentUser?.clinic_id, debouncedQuery, dateBounds, itemsPerPage]);

  // Load initial data (after loadData is declared)
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadData();
      toast({ title: "Success", description: "Data refreshed successfully" });
    } catch {
      toast({ title: "Error", description: "Failed to refresh data", variant: "destructive" });
    } finally {
      setRefreshing(false);
    }
  };

  // Derive patient list from loaded prescriptions — no bulk getPatients() needed
  const patients = useMemo(() => {
    const seen = new Set<string>();
    return prescriptions.reduce<{ id: string; name: string; age?: number | null; date_of_birth?: string | null; gender?: string | null; phone?: string | null }[]>((acc, p) => {
      if (p.patient_id && !seen.has(p.patient_id)) {
        seen.add(p.patient_id);
        acc.push({
          id: p.patient_id,
          name: p.patient_name || '',
          age: p.patient_age,
          date_of_birth: p.patient_date_of_birth,
          gender: p.patient_gender,
          phone: p.patient_phone,
        });
      }
      return acc;
    }, []);
  }, [prescriptions]);

  // Back to the first batch when the filters change
  useEffect(() => {
    setItemsPerPage(PAGE);
  }, [debouncedQuery, dateBounds]);
  const hasFilters = !!(debouncedQuery || dateBounds.from || dateBounds.to);



  const handleViewPrescription = (prescription: Prescription) => {
    setPrescriptionToPrint(prescription);
    setShowPrintModal(true);
  };

  const handleEditPrescription = (prescription: Prescription) => {
    // Eligibility (same clinic day, own prescription) is decided where the Edit button is shown — canEditPrescription
    navigate(`/prescriptions/edit/${prescription.id}`);
  };

  const handlePrintPrescription = (prescription: Prescription) => {
    setPrescriptionToPrint(prescription);
    setShowPrintModal(true);
  };



  // Simple filter handlers
  const handleFiltersChange = (filters: SimpleFilterState) => {
    setSimpleFilters(filters);
  };










  if (loading) {
    return (
      <div className="prescription-form-container">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading prescriptions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prescription-form-container">
        <div className="medical-card p-8 text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Failed to Load Prescriptions</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }


  return (
    <div className="prescription-form-container">
      <div className="mb-4">
        {/* Wraps on phones: title, then the actions */}
        <PageHeader
          icon={FileText}
          title="Prescriptions"
          subtitle={allTotal > 0
            ? `${allTotal} prescription${allTotal !== 1 ? 's' : ''} · ${writtenToday} written today`
            : undefined}
          actions={<>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="w-8 px-0"
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
            </Button>
            
            {showPatientSearch ? (
              <div className="w-full sm:w-72">
                <PatientSearchInput
                  value={patientSearchText}
                  onChange={setPatientSearchText}
                  onSelect={(patient) => {
                    setShowPatientSearch(false);
                    setPatientSearchText('');
                    navigate(`/patients/${patient.id}?openPrescription=true`);
                  }}
                  onEscape={() => { setShowPatientSearch(false); setPatientSearchText(''); }}
                  onBlur={() => { setShowPatientSearch(false); setPatientSearchText(''); }}
                  placeholder="Search patient for new prescription…"
                  inputSize="sm"
                  className="h-8"
                  containerClassName="w-full"
                  autoFocus
                />
              </div>
            ) : canPrescribe && (
              <Button
                onClick={() => setShowPatientSearch(true)}
                size="sm"
                className="gap-1.5"
              >
                <Plus className="w-4 h-4" />
                New prescription
              </Button>
            )}
          </>}
        />
      </div>

      <div className="space-y-3">

        {/* Simple Search & Filters */}
        <SimpleFilters
          filters={simpleFilters}
          onFiltersChange={handleFiltersChange}
          totalResults={matchCount}
        />



        {/* Simple Data Table */}
        <PrescriptionDataTable
          prescriptions={prescriptions}
          patients={patients}
          loading={loading}
          onView={handleViewPrescription}
          onEdit={canPrescribe ? handleEditPrescription : undefined}
          onPrint={handlePrintPrescription}
          hideSearchAndFilters={true}
          filtered={hasFilters}
          onCreate={() => setShowPatientSearch(true)}
        />
        {prescriptions.length < matchCount && (
          <Button variant="outline" size="sm" className="w-full" onClick={() => setItemsPerPage(n => n + PAGE)}>
            Show more · {prescriptions.length} of {matchCount}
          </Button>
        )}
      </div>

      {/* Print / View Prescription Modal */}
      <PrintPrescriptionPreviewModal
        open={showPrintModal}
        prescription={prescriptionToPrint}
        onClose={() => {
          setShowPrintModal(false);
          setPrescriptionToPrint(null);
        }}
      />

    </div>
  );
}